import React, { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { formatDateWithTimezone } from "shared/utility/date";
import { SMS_PROVIDERS } from "../constants";
import { DEFAULT_SMS_MSG_HEADER } from "shared/components/SmsSenderAccountSelect";

const PAGE_SIZE = 20;

const STATUS_CONFIG = {
  DELIVERED: { label: "smsDelivered", bg: "#e8f5e9", color: "#2e7d32" },
  QUEUED: { label: "smsQueued", bg: "#fff3e0", color: "#e65100" },
  REJECTED_BY_OPERATOR: { label: "smsRejected", bg: "#ffebee", color: "#d32f2f" },
  TIMEOUT: { label: "smsTimeout", bg: "#ffebee", color: "#d32f2f" },
  INVALID_NUMBER: { label: "smsInvalidNumber", bg: "#ffebee", color: "#d32f2f" },
  OPERATOR_FAILED: { label: "smsOperatorFailed", bg: "#ffebee", color: "#d32f2f" },
  SEND_ERROR: { label: "smsSendError", bg: "#ffebee", color: "#d32f2f" },
  DUPLICATE: { label: "smsDuplicate", bg: "#fff3e0", color: "#e65100" },
  BLACKLISTED: { label: "smsBlacklisted", bg: "#ffebee", color: "#d32f2f" },
  IYS_REJECTED: { label: "smsIysRejected", bg: "#ffebee", color: "#d32f2f" },
  IYS_ERROR: { label: "smsIysError", bg: "#ffebee", color: "#d32f2f" },
  UNKNOWN: { label: "smsUnknown", bg: "#f5f5f5", color: "#999" },
};

const BulkSmsReport = () => {
  const { t } = useTranslation();
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);

  const [smses, setSmses] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const loadReport = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/smsv2/bulk-report?page=${page}&limit=${PAGE_SIZE}`
        );
        setSmses(responseData.smses);
        setPagination(responseData.pagination);
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    },
    [sendRequest]
  );

  useEffect(() => {
    loadReport(1);
  }, [loadReport]);

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.UNKNOWN;
  };

  const timezone = auth.timezone || "Europe/Istanbul";

  return (
    <Box sx={{ py: 3, px: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, color: "var(--color-solid)" }}>
          {t("smsReports")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "#999" }}>
            {t("totalSmsCount", { count: pagination.totalCount })}
          </Typography>
          <IconButton onClick={() => loadReport(pagination.page)} disabled={isLoading} size="small">
            {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ flex: 1, borderRadius: 2, overflow: "hidden", minHeight: 0, display: "flex", flexDirection: "column" }} elevation={1}>
        <TableContainer sx={{ flex: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: 60 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>{t("phoneNumber")}</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>{t("messageText")}</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>
                  {t("smsReportMsgHeader")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>{t("status")}</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>{t("smsDate")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && smses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : smses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: "#999" }}>
                    {t("noSmsFound")}
                  </TableCell>
                </TableRow>
              ) : (
                smses.map((sms, index) => {
                  const statusCfg = getStatusConfig(sms.status);
                  const rowNumber = (pagination.page - 1) * PAGE_SIZE + index + 1;
                  const isNetgsm = sms.provider === SMS_PROVIDERS.NETGSM;
                  const headerDisplay = isNetgsm ? sms.netgsmMsgHeader || DEFAULT_SMS_MSG_HEADER : "—";
                  return (
                    <TableRow key={sms.id} sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}>
                      <TableCell sx={{ color: "#999" }}>{rowNumber}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {sms.phoneNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={sms.text}
                        >
                          {sms.text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isNetgsm ? "text.primary" : "#999",
                            fontWeight: isNetgsm ? 500 : 400,
                          }}
                        >
                          {headerDisplay}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(statusCfg.label)}
                          size="small"
                          sx={{
                            backgroundColor: statusCfg.bg,
                            color: statusCfg.color,
                            fontWeight: 500,
                          }}
                        />
                        {sms.error && sms.status !== "DELIVERED" && sms.status !== "QUEUED" && sms.error !== "UNKNOWN" && (
                          <Typography variant="caption" sx={{ display: "block", color: "#d32f2f", mt: 0.5 }}>
                            {sms.error}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                          {formatDateWithTimezone(sms.createdAt, timezone)}
                        </Typography>
                        {sms.deliveredDate && sms.status === "DELIVERED" && (
                          <Typography variant="caption" sx={{ color: "#2e7d32", display: "block" }}>
                            {t("deliveredAt")}: {formatDateWithTimezone(sms.deliveredDate, timezone)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              py: 1.5,
              borderTop: "1px solid #e0e0e0",
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              disabled={pagination.page <= 1 || isLoading}
              onClick={() => loadReport(pagination.page - 1)}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {pagination.page} / {pagination.totalPages}
            </Typography>
            <IconButton
              size="small"
              disabled={pagination.page >= pagination.totalPages || isLoading}
              onClick={() => loadReport(pagination.page + 1)}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BulkSmsReport;
